import { useEffect, useState } from 'react';
import api from '@/lib/api';

export interface MegaMenuLink {
  label: string;
  url: string;
  badge?: string;
  heading?: boolean;
}

export interface MegaMenuColumn {
  heading: string;
  links: MegaMenuLink[];
}

export interface MegaFeaturedCard {
  image?: string;
  title?: string;
  url?: string;
}

export interface MegaMenuItem {
  title: string;
  url: string;
  badge?: string;
  columns: MegaMenuColumn[];
  /** Up to 4 promo cards shown on the panel's right (or alone, full width). */
  featured: MegaFeaturedCard[];
}

// The API serves featured as an array; older cached responses may still carry
// a single object or null. Coerce so consumers can always .map().
// (Exported for tests — app code goes through useMegaMenu.)
export function normalizeItem(raw: MegaMenuItem & { featured?: unknown }): MegaMenuItem {
  const f = raw.featured;
  const featured = Array.isArray(f) ? f : f && typeof f === 'object' ? [f as MegaFeaturedCard] : [];
  return { ...raw, columns: Array.isArray(raw.columns) ? raw.columns : [], featured };
}

// Module-level promise cache — the header mounts on every page but the menu
// should be fetched once per page load. A failed fetch clears the cache so a
// later mount can retry. (Same pattern as useSiteMenus.)
let menuPromise: Promise<MegaMenuItem[] | null> | null = null;

function loadMenu(): Promise<MegaMenuItem[] | null> {
  if (!menuPromise) {
    menuPromise = api
      .get<{ items: MegaMenuItem[] }>('/mega-menu')
      .then((res) => (Array.isArray(res.data?.items) ? res.data.items.map(normalizeItem) : []))
      .catch(() => {
        menuPromise = null;
        return null;
      });
  }
  return menuPromise;
}

/**
 * Admin-managed header mega menu (Content → Header Menu). The API falls back
 * to a category-derived menu when nothing is saved, so a non-null result is
 * the norm; null means the request failed and the caller should keep its
 * hardcoded fallback links.
 */
export function useMegaMenu(): MegaMenuItem[] | null {
  const [items, setItems] = useState<MegaMenuItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadMenu().then((m) => {
      if (!cancelled && m && m.length > 0) setItems(m);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return items;
}
